import { ModelLookupContext, ModelLookupProduct } from "@auer-steel/Contexts/ModelLookup/ModelLookupContext";
import ModelSearchListDetail from "@auer-steel/CustomComponents/ModelLookup/ModelSearchListDetail";
import ModelLookup from "@auer-steel/CustomTypes/ModelLookup";
import ModelPartsLookup from "@auer-steel/CustomTypes/ModelPartsLookup";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { ProductPrimaryImageStyles } from "@insite/content-library/Components/ProductPrimaryImage";
import Button, { ButtonProps } from "@insite/mobius/Button";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import { LinkProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

export interface ModelSearchListStyles {
    titleItem?: GridItemProps;
    title?: TypographyProps;
    container?: InjectableCss;
    productLineContainer?: GridContainerProps;
    groupOptionItems?: GridItemProps;
    productLineItemColumn?: GridItemProps;
    innerProductLineContainer?: GridContainerProps;
    productLineImageItem?: GridItemProps;
    productLineImageClickable?: ClickablePresentationProps;
    productLineImage?: LazyImageProps;
    productLineNameLinkItem?: GridItemProps;
    productLineNameLink?: LinkProps;
    viewAllButtonItem?: GridItemProps;
    viewAllButton?: ButtonProps;
    fullPrimaryImageModal?: ModalPresentationProps;
    fullPrimaryImage?: ProductPrimaryImageStyles;
    pdfWrapper?: GridItemProps;
    objectStyle?: InjectableCss;
    centeringWrapper?: InjectableCss;
    loadingSpinner?: LoadingSpinnerProps;
    groupOptionItem?: GridItemProps;
    viewPartsButton?: ButtonProps;
}

const CenteringWrapper = styled.div<InjectableCss>`
    ${({ css }) => css}
`;

export const modelSearchListStyles: ModelSearchListStyles = {
    titleItem: {
        width: 12,
        css: css`
            width: 100%;
            justify-content: center;
        `,
    },
    title: {
        variant: "h3",
        css: css`
            margin-bottom: 0;
        `,
    },
    container: {
        css: css`
            margin: 5px;
        `,
    },
    productLineContainer: {
        gap: 10,
    },
    innerProductLineContainer: {
        gap: 0,
        css: css`
            width: 100%;
        `,
    },
    productLineItemColumn: {
        width: [6, 6, 12, 12, 12],
    },
    groupOptionItems: {
        css: css`
            margin: 20px 15px;
        `,
    },
    productLineImageItem: {
        width: 12,
        align: "middle",
        css: css`
            width: 100%;
            height: 160px;
            justify-content: center;
        `,
    },
    productLineImageClickable: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    productLineNameLinkItem: {
        width: 12,
        css: css`
            width: 100%;
            padding-bottom: 5px;
        `,
    },
    productLineNameLink: {
        typographyProps: {
            weight: "bold",
            css: css`
                width: 100%;
                overflow-wrap: break-word;
                word-wrap: break-word;
                text-align: center;
            `,
        },
        color: "text.main",
    },
    viewAllButtonItem: {
        width: 12,
        css: css`
            padding-top: 5px;
            width: 100%;
            justify-content: center;
        `,
    },
    viewAllButton: {
        typographyProps: {
            weight: "bold",
            ellipsis: true,
        },
    },
    fullPrimaryImageModal: {
        cssOverrides: {
            modalContainer: css`
                width: auto;
            `,
            modalBody: css`
                width: auto;
                max-width: initial;
                max-height: initial !important;
            `,
            modalTitle: css`
                border-bottom: none;
                padding: 10px 10px 0;
            `,
            modalContent: css`
                text-align: center;
            `,
        },
    },
    fullPrimaryImage: {
        image: {
            css: css`
                img {
                    width: auto;
                    max-height: 80vh;
                }
            `,
        },
    },
    pdfWrapper: {
        css: css`
            width: 400px;
            z-index: 1;
        `,
    },
    objectStyle: {
        css: css`
            background-color: #fff;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 75px;
            margin: 0 auto;
            padding-top: 15px;
            clear: both;
            width: 100%;
            text-align: center;
        `,
    },
    groupOptionItem: {
        width: [12, 12, 6, 6, 4, 4],
        css: css`
            display: flex-box;
            padding: 5px;
            justify-content: center;
            flex-wrap: wrap;
            align-content: flex-start;

            button.selected {
                background-color: ${getColor("secondary.main")};
            }
        `,
    },
    viewPartsButton: {
        css: css`
            flex: 0, 1, auto;
            background-color: #616161;
            width: 100%;
            border: none;
        `,
    },
};

const styles = modelSearchListStyles;

const mapStateToProps = (state: ApplicationState) => {
    return <></>;
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & HasHistory;

const ModelSearchResultsList: React.FC<Props> = ({ history }) => {
    const modelLookupContext = React.useContext(ModelLookupContext);
    const {
        modelNumber,
        modelLookupResultsData,
        modelPartsLookupResultsData,
        handleSetDisplayDetailsView,
        isLoading,
        currentModelLookupProduct,
        modelLookupRootUri,
        currentSelectedGroup,
        handleSetCurrentModelLookupProduct,
        handleSetCurrentSelectedGroup,
    } = modelLookupContext;

    const lookupResults: ModelLookup = { ...modelLookupResultsData } as ModelLookup;
    const result = lookupResults.result;

    if (isLoading) {
        return (
            <>
                <CenteringWrapper {...styles.centeringWrapper}>
                    <LoadingSpinner {...styles.loadingSpinner} />
                </CenteringWrapper>
            </>
        );
    }

    if (!result) {
        return <></>;
    }

    const groupedByDrawingLink = result[0].rows.reduce(
        (resultItem: { [x: string]: ModelLookupProduct[] }, currentValue: any) => {
            if ({ ...currentValue }.drawingLink && { ...currentValue }.groups) {
                if (!resultItem[currentValue.groups]) {
                    resultItem[currentValue.groups] = Array<ModelLookupProduct>();
                }

                const item = { drawLink: currentValue.drawingLink, group: { ...currentValue }.groups };
                resultItem[currentValue.groups].push({ ...currentValue } as ModelLookupProduct);
            }

            return resultItem;
        },
        {},
    );

    const groupedByCriticalParts = result[0].rows.reduce(
        (resultItem: { [x: string]: ModelLookupProduct[] }, currentValue: any) => {
            if ({ ...currentValue }.criticalPart === "Yes" && { ...currentValue }.groups) {
                if (!resultItem["Critical Parts"]) {
                    resultItem["Critical Parts"] = Array<ModelLookupProduct>();
                }

                resultItem["Critical Parts"].push({ ...currentValue } as ModelLookupProduct);
            }

            return resultItem;
        },
        {},
    );

    const groupClickHandler = (data: ModelLookupProduct[], group: string) => {
        handleSetCurrentSelectedGroup(group);
        handleSetCurrentModelLookupProduct(data);
        handleSetDisplayDetailsView(true);
        return <ModelSearchListDetail />;
    };

    return (
        <GridContainer {...styles.groupOptionItems}>
            {Object.entries(groupedByDrawingLink).map(([key, value]) => {
                return (
                    <GridItem {...styles.groupOptionItem}>
                        <Button
                            className={currentSelectedGroup === value[0]?.groups ? "selected" : ""}
                            {...styles.viewPartsButton}
                            onClick={() => groupClickHandler(value, value[0]?.groups)}
                            data-test-selector="modelDetailLink"
                        >
                            {translate(value[0]?.groups)}
                        </Button>
                    </GridItem>
                );
            })}
            {Object.entries(groupedByCriticalParts).map(([key, value]) => {
                return (
                    <GridItem {...styles.groupOptionItem}>
                        <Button
                            className={currentSelectedGroup === "CRITICAL PARTS" ? "selected" : ""}
                            {...styles.viewPartsButton}
                            onClick={() => groupClickHandler(value, "CRITICAL PARTS")}
                            data-test-selector="modelDetailLink"
                        >
                            {translate("CRITICAL PARTS")}
                        </Button>
                    </GridItem>
                );
            })}
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(ModelSearchResultsList));
